import type { ReactNode } from 'react'
import { StarIcon } from './icons'
import './MessageBubble.css'

// The four visual roles a message can take, shared by the chat-room and
// agent-transcript surfaces so both render an identical iMessage-style
// group chat:
//   mine    — the logged-in human. Right-aligned, tint fill, no name label.
//   agent   — an AI agent. Left, purple tint, star badge + name.
//   theirs  — any other participant (other humans, or other agents). Left,
//             neutral fill, name label. One flat color for everyone else.
//   deleted — a tombstoned message. Left, neutral, muted.
export type BubbleVariant = 'mine' | 'agent' | 'theirs' | 'deleted'

export function MessageBubble({
  variant,
  senderName,
  time,
  gapTop,
  highlight,
  children,
  belowBubble,
}: {
  variant: BubbleVariant
  // Shown above the bubble for every non-me variant when provided. Omitted for
  // `mine` (iMessage never labels your own messages) and for 1:1 transcripts
  // where the caller chooses not to pass one.
  senderName?: string
  time: string
  // Extra top margin when the sender changed from the previous row.
  gapTop?: boolean
  // Accent the bubble because it @mentions the viewer.
  highlight?: boolean
  // The bubble body (plain text span, markdown, deleted notice, reply quote…).
  children: ReactNode
  // Optional content rendered under the bubble, outside it (reaction pills).
  belowBubble?: ReactNode
}) {
  const isMine = variant === 'mine'
  const isAgent = variant === 'agent'
  return (
    <div
      className={`msgb-wrap${isMine ? ' msgb-wrap--mine' : ''}${gapTop ? ' msgb-wrap--gap-top' : ''}`}
    >
      {!isMine && senderName && (
        <div className="msgb__sender">
          {isAgent && <StarIcon className="msgb__agent-badge-icon" />}
          {senderName}
        </div>
      )}
      <div className={`msgb msgb--${variant}${highlight ? ' msgb--mentioned' : ''}`}>
        {children}
      </div>
      <div className={`msgb__time${isMine ? ' msgb__time--mine' : ''}`}>{time}</div>
      {belowBubble}
    </div>
  )
}
